import { Location } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { CardComponent } from '@app/components/card';
import { ChipColor, ChipComponent, ChipSize } from '@app/components/chip';
import { SpinnerComponent } from '@app/components/spinner';
import { TitleComponent, TitleSpacing } from '@app/components/title-section';
import { ToastService, ToastType } from '@app/components/toast';
import { AdminChallengesService } from '@app/services/admin-challenges/admin-challenges.service';
import { TranslatorService } from '@app/services/translator/translator.service';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { GroupChallengeTeamV2, GroupChallengeWithTeams } from '@skyfit/core-data-library';
import { catchError, map, of, switchMap } from 'rxjs';
import { ChallengeLeaderboardCardComponent } from './challenge-leaderboard-card/challenge-leaderboard-card.component';

export enum GroupChallengeStatus {
  NOT_STARTED = 'notStarted',
  STARTED = 'started',
  ENDED = 'ended',
  NOT_SET = 'notSet',
  LIVE = 'live',
}

@Component({
  selector: 'app-challenge-live-details',
  templateUrl: './challenge-live-details.component.html',
  styleUrl: './challenge-live-details.component.css',
  standalone: true,
  imports: [
    CardComponent,
    TitleComponent,
    TranslateDirective,
    TranslatePipe,
    SpinnerComponent,
    ChipComponent,
    ChallengeLeaderboardCardComponent,
  ],
})
export class ChallengeLiveDetailsComponent implements OnInit {
  private readonly challengesService = inject(AdminChallengesService);
  private readonly location = inject(Location);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toastService = inject(ToastService);
  private readonly translatorService = inject(TranslatorService);

  isLoading = signal(true);
  teams = signal<GroupChallengeTeamV2[]>([]);
  challengeStatus = signal<GroupChallengeStatus>(GroupChallengeStatus.NOT_SET);
  hasNoParticipants = computed(() => !this.isLoading() && this.teams().length === 0);
  isLiveChallenge = computed(() => this.challengeStatus() === GroupChallengeStatus.LIVE);

  challenge?: GroupChallengeWithTeams;
  challengeId?: number;
  currentDate = new Date();
  groupChallengeDates: string | undefined;
  openTeamBoards: number[] = [];

  ChipColor = ChipColor;
  ChipSize = ChipSize;
  TitleSpacing = TitleSpacing;
  groupChallengeStatus = GroupChallengeStatus;

  ngOnInit(): void {
    this.route.params
      .pipe(
        switchMap((params) => {
          this.challengeId = params.id;
          return this.challengesService.getGroupChallengeScores(params.id).pipe(
            catchError(() =>
              this.challengesService.getGroupChallenge(params.id).pipe(
                map((challenge) => ({ ...challenge, teams: [] }) as GroupChallengeWithTeams),
                catchError(() => {
                  this.toastService.add(
                    this.translatorService.translate('challenges.challengeCouldNotBeLoaded'),
                    ToastType.ERROR
                  );
                  this.goBack();
                  return of(null);
                })
              )
            )
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((challenge) => {
        if (!challenge) return;
        this.challenge = challenge;
        this.teams.set(challenge.teams ?? []);
        this.isLoading.set(false);
        const rawStartDate = challenge?.groupChallengeStarts?.[0]?.startTime;
        if (rawStartDate) {
          const startDate = new Date(rawStartDate);
          const endDate = new Date(startDate);
          endDate.setDate(endDate.getDate() + challenge.durationInDays);
          this.challengeStatus.set(this.getStatus(startDate, endDate));
          this.groupChallengeDates = this.formatDateRange(startDate, endDate);
        }
      });
  }

  onToggleTeamLeaderboard(teamIndex: number): void {
    const copy = [...this.openTeamBoards];
    const idx = copy.indexOf(teamIndex);
    if (idx > -1) {
      copy.splice(idx, 1);
    } else {
      copy.push(teamIndex);
    }
    this.openTeamBoards = copy;
  }

  goBack(): void {
    this.location.back();
  }

  private getStatus(startDate: Date, endDate: Date): GroupChallengeStatus {
    if (this.currentDate < startDate) {
      return GroupChallengeStatus.NOT_STARTED;
    }
    if (this.currentDate > endDate) {
      return GroupChallengeStatus.ENDED;
    }
    return GroupChallengeStatus.LIVE;
  }

  private formatDateRange(startDate: Date, endDate: Date): string {
    const options: Intl.DateTimeFormatOptions = { month: 'short' };
    const startMonth = startDate.toLocaleString('en-US', options);
    const endMonth = endDate.toLocaleString('en-US', options);
    return `${startMonth} ${startDate.getDate()} - ${endMonth} ${endDate.getDate()}, ${endDate.getFullYear()}`;
  }
}
